/**
 * ValidationContext - Manages document data, field values, and validation state
 * Based on requirements 4.1, 4.2, 4.3, 5.1
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  ValidationContextState,
  ValidationContextAction,
  ValidationContextValue,
  ExtractedDocumentData
} from '../types';
import { 
  validateField as validateFieldUtil, 
  validateAllFields as validateAllFieldsUtil,
  areAllFieldsValid 
} from '../utils/validationUtils';
import { 
  createValidatedData, 
  normalizeAllFieldValues 
} from '../utils/transformationUtils';
import { 
  createEditHistoryTracker, 
  EditHistoryTracker 
} from '../utils/auditTrailUtils';
import { useDocumentService } from '../services/documentService';
import { useAuthMode } from '../hooks/useAuthMode';
import { useNotificationHelpers } from '../components/feedback';

// Use localAccountId (Entra OID) as the identity key — it matches what the backend's
// JWT extractor (tenantContext.extractUserId) reads from jwt.oid, so reports can
// correlate validatedBy with the viewer's userId without a UPN ↔ OID lookup.
function getClerkId(user: { localAccountId?: string; homeAccountId?: string; username?: string } | null | undefined): string {
  return user?.localAccountId || user?.homeAccountId || user?.username || 'unknown';
}

// Initial state
const initialState: ValidationContextState = {
  document: null,
  validatedFields: {},
  reviewedFields: {},
  fieldErrors: {},
  isSubmitting: false,
  isDirty: false,
  isLoading: false,
  lastSaved: null,
};

// Validation reducer
function validationReducer(
  state: ValidationContextState,
  action: ValidationContextAction
): ValidationContextState {
  switch (action.type) {
    case 'LOAD_DOCUMENT':
      return {
        ...state,
        document: action.payload,
        validatedFields: action.payload.fields.reduce((acc, field) => {
          // Coerce non-string values (arrays from DB schema changes) to string
          const raw = field.value as unknown;
          if (typeof raw === 'string') {
            acc[field.id] = raw;
          } else if (Array.isArray(raw)) {
            acc[field.id] = raw.join(', ');
          } else if (raw == null) {
            acc[field.id] = '';
          } else {
            acc[field.id] = String(raw);
          }
          return acc;
        }, {} as Record<string, string>),
        reviewedFields: {},
        fieldErrors: {},
        isDirty: false,
        isLoading: false,
      };

    case 'UPDATE_FIELD':
      return {
        ...state,
        validatedFields: {
          ...state.validatedFields,
          [action.payload.fieldId]: action.payload.value,
        },
        // Editing a field counts as reviewing it (UX4)
        reviewedFields: {
          ...state.reviewedFields,
          [action.payload.fieldId]: true,
        },
        isDirty: true,
        // Clear field error when user starts typing
        fieldErrors: {
          ...state.fieldErrors,
          [action.payload.fieldId]: '',
        },
      };

    case 'CONFIRM_FIELD':
      return {
        ...state,
        reviewedFields: {
          ...state.reviewedFields,
          [action.payload.fieldId]: true,
        },
      };

    case 'SET_FIELD_ERROR':
      return {
        ...state,
        fieldErrors: {
          ...state.fieldErrors,
          [action.payload.fieldId]: action.payload.error,
        },
      };

    case 'CLEAR_FIELD_ERROR':
      return {
        ...state,
        fieldErrors: {
          ...state.fieldErrors,
          [action.payload.fieldId]: '',
        },
      };

    case 'SET_SUBMITTING':
      return {
        ...state,
        isSubmitting: action.payload,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'MARK_SAVED':
      return {
        ...state,
        isDirty: false,
        lastSaved: new Date().toISOString(),
      };

    case 'RESET_FORM':
      if (!state.document) return state;
      return {
        ...state,
        validatedFields: state.document.fields.reduce((acc, field) => {
          const raw = field.value as unknown;
          if (typeof raw === 'string') {
            acc[field.id] = raw;
          } else if (Array.isArray(raw)) {
            acc[field.id] = raw.join(', ');
          } else if (raw == null) {
            acc[field.id] = '';
          } else {
            acc[field.id] = String(raw);
          }
          return acc;
        }, {} as Record<string, string>),
        reviewedFields: {},
        fieldErrors: {},
        isDirty: false,
      };

    case 'VALIDATE_ALL_FIELDS':
      // This will be handled by the validateAllFields function
      return state;

    default:
      return state;
  }
}



// Auto-save hook with debouncing
function useAutoSave(
  validatedFields: Record<string, string>,
  isDirty: boolean,
  onSave: () => void,
  delay: number = 2000
) {
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isDirty) {
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout for auto-save
      timeoutRef.current = setTimeout(() => {
        onSave();
      }, delay);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [validatedFields, isDirty, onSave, delay]);
}

// Context creation
const ValidationContext = createContext<ValidationContextValue | undefined>(undefined);

// Provider component
interface ValidationProviderProps {
  children: React.ReactNode;
}

export function ValidationProvider({ children }: ValidationProviderProps) {
  const [state, dispatch] = useReducer(validationReducer, initialState);
  const { loadDocument: loadDocumentAPI, submitValidation: submitValidationAPI, submitToAugmentum } = useDocumentService();
  const { user } = useAuthMode();
  const editHistoryTrackerRef = useRef<EditHistoryTracker | null>(null);
  const notifications = useNotificationHelpers();

  // Initialize edit history tracker when document is loaded
  useEffect(() => {
    if (state.document && user) {
      editHistoryTrackerRef.current = createEditHistoryTracker(
        state.document.documentId,
        user.localAccountId || user.homeAccountId || 'unknown',
        user.name || user.username || 'unknown'
      );
    }
  }, [state.document, user]);

  // Auto-save functionality
  const handleAutoSave = useCallback(async () => {
    if (state.isDirty && state.document && user) {
      try {
        // Normalize field values before saving
        const normalizedFields = normalizeAllFieldValues(state.validatedFields, state.document.fields);
        
        // Create validated data object
        const validatedData = createValidatedData(
          state.document.documentId,
          normalizedFields,
          getClerkId(user),
          state.document
        );

        // Submit to validation service (for auto-save)
        await submitValidationAPI(validatedData);
        
        dispatch({ type: 'MARK_SAVED' });
        notifications.showAutoSave();
        console.log('Auto-saved validated fields');
      } catch (error) {
        console.error('Auto-save failed:', error);
        // Don't show error notification for auto-save failures to avoid disrupting user experience
      }
    }
  }, [state.isDirty, state.validatedFields, state.document, user, submitValidationAPI, notifications]);

  useAutoSave(state.validatedFields, state.isDirty, handleAutoSave);

  // Action creators
  const loadDocument = useCallback(async (documentId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const data = await loadDocumentAPI(documentId);
      dispatch({ type: 'LOAD_DOCUMENT', payload: data });
      notifications.showDocumentLoaded(data.metadata.fileName);
    } catch (error) {
      console.error('Failed to load document:', error);
      notifications.showError('Failed to load document. Please try again.');
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [loadDocumentAPI, notifications]);

  const loadDocumentData = useCallback(async (data: ExtractedDocumentData) => {
    // Use raw values from AI extraction (formatting handled by Azure AI Content Understanding)
    const formattedFields = data.fields;
    
    const formattedData = {
      ...data,
      fields: formattedFields
    };
    
    dispatch({ type: 'LOAD_DOCUMENT', payload: formattedData });
  }, []);

  const updateField = useCallback((fieldId: string, value: string) => {
    // Track field change in edit history
    if (editHistoryTrackerRef.current && state.document) {
      const field = state.document.fields.find(f => f.id === fieldId);
      if (field) {
        const previousValue = state.validatedFields[fieldId] || field.value;
        editHistoryTrackerRef.current.trackFieldChange(
          fieldId,
          field.name,
          previousValue,
          value,
          field.confidence
        );
      }
    }

    dispatch({ type: 'UPDATE_FIELD', payload: { fieldId, value } });
  }, [state.document, state.validatedFields]);

  // Mark a field as reviewed without changing its value (UX4 Enter-to-confirm).
  const confirmField = useCallback((fieldId: string) => {
    dispatch({ type: 'CONFIRM_FIELD', payload: { fieldId } });
  }, []);

  const validateFieldById = useCallback((fieldId: string) => {
    if (!state.document) return;

    const field = state.document.fields.find(f => f.id === fieldId);
    if (!field) return;

    const value = state.validatedFields[fieldId] || '';
    const validationResult = validateFieldUtil(field, value);

    if (!validationResult.isValid) {
      dispatch({
        type: 'SET_FIELD_ERROR',
        payload: { fieldId, error: validationResult.error || 'Validation failed' }
      });
    } else {
      dispatch({ type: 'CLEAR_FIELD_ERROR', payload: { fieldId } });
    }
  }, [state.document, state.validatedFields]);

  const validateAllFields = useCallback(() => {
    if (!state.document) return false;

    const validationResults = validateAllFieldsUtil(state.document.fields, state.validatedFields);
    const errors: string[] = [];
    
    // Update field errors based on validation results
    Object.entries(validationResults).forEach(([fieldId, result]) => {
      if (!result.isValid) {
        const errorMessage = result.error || 'Validation failed';
        dispatch({
          type: 'SET_FIELD_ERROR',
          payload: { fieldId, error: errorMessage }
        });
        errors.push(errorMessage);
      } else {
        dispatch({ type: 'CLEAR_FIELD_ERROR', payload: { fieldId } });
      }
    });

    const isValid = areAllFieldsValid(validationResults);
    
    // Show validation errors if any
    if (!isValid && errors.length > 0) {
      notifications.showValidationErrors(errors);
    }

    return isValid;
  }, [state.document, state.validatedFields, notifications]);

  const submitValidation = useCallback(async () => {
    if (!state.document || !user) return;

    dispatch({ type: 'SET_SUBMITTING', payload: true });

    try {
      // Validate all fields before submission
      const isValid = validateAllFields();
      if (!isValid) {
        throw new Error('Please fix validation errors before submitting');
      }

      // Normalize field values
      const normalizedFields = normalizeAllFieldValues(state.validatedFields, state.document.fields);

      // Create validated data object
      const validatedData = createValidatedData(
        state.document.documentId,
        normalizedFields,
        getClerkId(user),
        state.document
      );

      // Submit to validation service
      const submissionId = await submitValidationAPI(validatedData);
      console.log('Validation submitted with ID:', submissionId);

      // Create audit trail if edit history tracker exists
      if (editHistoryTrackerRef.current) {
        const auditTrail = editHistoryTrackerRef.current.createAuditTrail(
          state.document.fields,
          normalizedFields
        );
        console.log('Audit trail created:', auditTrail);
      }

      dispatch({ type: 'MARK_SAVED' });
      notifications.showValidationSubmitted(submissionId);
      return submissionId;
    } catch (error) {
      console.error('Validation submission failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Validation submission failed';
      notifications.showError(errorMessage);
      throw error;
    } finally {
      dispatch({ type: 'SET_SUBMITTING', payload: false });
    }
  }, [state.document, state.validatedFields, user, validateAllFields, submitValidationAPI, notifications]);

  const submitToAugmentumSystem = useCallback(async () => {
    if (!state.document || !user) return;

    dispatch({ type: 'SET_SUBMITTING', payload: true });

    try {
      // Validate all fields before submission
      const isValid = validateAllFields();
      if (!isValid) {
        throw new Error('Please fix validation errors before submitting to Augmentum');
      }

      // Normalize field values
      const normalizedFields = normalizeAllFieldValues(state.validatedFields, state.document.fields);

      // Create validated data object
      const validatedData = createValidatedData(
        state.document.documentId,
        normalizedFields,
        user.username || 'unknown',
        state.document
      );

      // Submit to Augmentum system
      const transactionId = await submitToAugmentum(validatedData);
      console.log('Submitted to Augmentum with transaction ID:', transactionId);

      dispatch({ type: 'MARK_SAVED' });
      return transactionId;
    } catch (error) {
      console.error('Augmentum submission failed:', error);
      throw error;
    } finally {
      dispatch({ type: 'SET_SUBMITTING', payload: false });
    }
  }, [state.document, state.validatedFields, user, validateAllFields, submitToAugmentum]);

  const resetForm = useCallback(() => {
    dispatch({ type: 'RESET_FORM' });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  }, []);

  const getEditHistory = useCallback(() => {
    return editHistoryTrackerRef.current?.getEditHistory() || [];
  }, []);

  const getSessionStats = useCallback(() => {
    return editHistoryTrackerRef.current?.getSessionStats() || {
      totalEdits: 0,
      fieldsModified: 0,
      sessionDurationMinutes: 0,
    };
  }, []);

  const contextValue: ValidationContextValue = useMemo(() => ({
    state,
    actions: {
      loadDocument,
      loadDocumentData,
      updateField,
      confirmField,
      validateField: validateFieldById,
      submitValidation,
      submitToAugmentumSystem,
      resetForm,
      setLoading,
      getEditHistory,
      getSessionStats,
    },
  }), [
    state,
    loadDocument,
    loadDocumentData,
    updateField,
    confirmField,
    validateFieldById,
    submitValidation,
    submitToAugmentumSystem,
    resetForm,
    setLoading,
    getEditHistory,
    getSessionStats,
  ]);

  return (
    <ValidationContext.Provider value={contextValue}>
      {children}
    </ValidationContext.Provider>
  );
}

// Hook for using the context
export function useValidation() {
  const context = useContext(ValidationContext);
  if (context === undefined) {
    throw new Error('useValidation must be used within a ValidationProvider');
  }
  return context;
}